'use client'

import { forwardRef, useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { ChevronLeft, ChevronRight, Calendar, X, RotateCcw } from 'lucide-react'
import { safeAccess, safeArrayAccess } from '../utils/security'

/**
 * BIRHAUS Calendar Component
 * 
 * Spanish-first date picker implementing all 10 BIRHAUS principles:
 * - Cognitive load reduction: Show 1 month, max 3 actions
 * - Spanish-first: Meses y días en español
 * - Undo functionality: 10-second undo for date changes
 * - Accessibility: WCAG AA+ keyboard navigation
 * - Performance: Optimized with React.memo and useMemo
 * - Progressive disclosure: Year/month selection on demand
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

// Spanish date utilities following BIRHAUS Spanish-first principle
const MESES_ESPANOL = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

const MESES_CORTOS_ESPANOL = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
]

const DIAS_ESPANOL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
const DIAS_CORTOS_ESPANOL = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

interface UndoState {
  previousDate: Date | null
  timeout: NodeJS.Timeout | null
  isActive: boolean
}

export interface BirhausCalendarProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // Date management
  value?: Date | null
  defaultValue?: Date | null
  onChange?: (date: Date | null) => void
  
  // Validation
  minDate?: Date
  maxDate?: Date
  disabledDates?: Date[]
  disabledDaysOfWeek?: number[] // 0 = Sunday, 6 = Saturday
  
  // BIRHAUS Principle #4: Progressive disclosure
  showTodayButton?: boolean
  showClearButton?: boolean
  showMonthYearSelector?: boolean
  
  // BIRHAUS Principle #5: Undo over confirm
  enableUndo?: boolean
  undoTimeoutMs?: number
  onUndo?: (previousDate: Date | null) => void
  
  // BIRHAUS Principle #1: Form serves flow
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'minimal' | 'inline'
  
  // BIRHAUS Principle #6: Accessibility = dignity
  describedBy?: string
  describedByEs?: string
  describedByEn?: string
  
  // BIRHAUS Principle #2: Honest data
  error?: string
  errorEs?: string
  errorEn?: string
  
  // Format and locale
  dateFormat?: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'
  locale?: 'es' | 'en'
  firstDayOfWeek?: 0 | 1 // 0 = Sunday, 1 = Monday
  
  // Enhanced functionality
  placeholder?: string
  placeholderEs?: string
  placeholderEn?: string
  disabled?: boolean
  required?: boolean
  
  // Cognitive load tracking
  cognitiveWeight?: number
  trackInteraction?: boolean
  
  // Event handlers
  onFocus?: () => void
  onBlur?: () => void
  onMonthChange?: (month: number, year: number) => void
  onYearChange?: (year: number) => void
}

export const BirhausCalendar = forwardRef<HTMLDivElement, BirhausCalendarProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Date management
  value,
  defaultValue,
  onChange,
  
  // Validation
  minDate,
  maxDate,
  disabledDates = [],
  disabledDaysOfWeek = [],
  
  // Progressive disclosure
  showTodayButton = true,
  showClearButton = true,
  showMonthYearSelector = false,
  
  // Undo functionality
  enableUndo = true,
  undoTimeoutMs = 10000,
  onUndo,
  
  // Appearance
  size = 'md',
  variant = 'default',
  
  // Accessibility
  describedBy,
  describedByEs,
  describedByEn,
  
  // Validation
  error,
  errorEs,
  errorEn,
  
  // Format and locale
  dateFormat = 'DD/MM/YYYY',
  locale = 'es',
  firstDayOfWeek = 1, // Monday first (common in Latin America)
  
  // Enhanced functionality
  placeholder,
  placeholderEs,
  placeholderEn,
  disabled = false,
  required = false,
  
  // Cognitive load
  cognitiveWeight = 2,
  trackInteraction = false,
  
  // Event handlers
  onFocus,
  onBlur,
  onMonthChange,
  onYearChange,
  
  ...props
}, ref) => {
  // State management
  const [selectedDate, setSelectedDate] = useState<Date | null>(value || defaultValue || null)
  const [currentMonth, setCurrentMonth] = useState<number>(() => 
    selectedDate?.getMonth() ?? new Date().getMonth()
  )
  const [currentYear, setCurrentYear] = useState<number>(() => 
    selectedDate?.getFullYear() ?? new Date().getFullYear()
  )
  const [isOpen, setIsOpen] = useState(false)
  const [undoState, setUndoState] = useState<UndoState>({
    previousDate: null,
    timeout: null,
    isActive: false
  })
  
  // Refs
  const calendarRef = useRef<HTMLDivElement>(null)
  const interactionStartTime = useRef<number>()
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, 'Seleccionar fecha')
  const displayPlaceholder = getDisplayText(placeholder, placeholderEs, placeholderEn, 'DD/MM/AAAA')
  const displayError = getDisplayText(error, errorEs, errorEn, '')
  const displayDescribedBy = getDisplayText(describedBy, describedByEs, describedByEn, '')
  
  // Format date for display
  const formatDate = useCallback((date: Date | null): string => {
    if (!date) return ''
    
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear().toString()
    
    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`
      case 'DD/MM/YYYY':
      default:
        return `${day}/${month}/${year}`
    }
  }, [dateFormat])
  
  // Check if date is disabled
  const isDateDisabled = useCallback((date: Date): boolean => {
    if (disabled) return true
    if (minDate && date < minDate) return true
    if (maxDate && date > maxDate) return true
    if (disabledDaysOfWeek.includes(date.getDay())) return true
    if (disabledDates.some(disabledDate => 
      date.toDateString() === disabledDate.toDateString()
    )) return true
    return false
  }, [disabled, minDate, maxDate, disabledDaysOfWeek, disabledDates])
  
  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1)
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0)
    const firstDayOfWeekInMonth = (firstDayOfMonth.getDay() - firstDayOfWeek + 7) % 7
    
    const days: (Date | null)[] = []
    
    // Add empty cells for days from previous month
    for (let i = 0; i < firstDayOfWeekInMonth; i++) {
      days.push(null)
    }
    
    // Add days of current month
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      days.push(new Date(currentYear, currentMonth, day))
    }
    
    return days
  }, [currentMonth, currentYear, firstDayOfWeek])
  
  // BIRHAUS Principle #5: Undo functionality
  const handleDateChange = useCallback((newDate: Date | null) => {
    if (enableUndo && selectedDate !== newDate) {
      // Clear existing timeout
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
      
      // Set up new undo state
      const timeout = setTimeout(() => {
        setUndoState(prev => ({ ...prev, isActive: false, timeout: null }))
      }, undoTimeoutMs)
      
      setUndoState({
        previousDate: selectedDate,
        timeout,
        isActive: true
      })
    }
    
    setSelectedDate(newDate)
    onChange?.(newDate)
    
    // Track cognitive load if enabled
    if (trackInteraction && interactionStartTime.current) {
      const interactionTime = Date.now() - interactionStartTime.current
      console.debug('[BIRHAUS] Calendar interaction time:', interactionTime, 'ms')
    }
  }, [selectedDate, enableUndo, undoTimeoutMs, onChange, trackInteraction])
  
  // Handle undo action
  const handleUndo = useCallback(() => {
    if (undoState.isActive && undoState.timeout) {
      clearTimeout(undoState.timeout)
      setSelectedDate(undoState.previousDate)
      onChange?.(undoState.previousDate)
      onUndo?.(undoState.previousDate)
      setUndoState({ previousDate: null, timeout: null, isActive: false })
    }
  }, [undoState, onChange, onUndo])
  
  // Navigation handlers
  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    let newMonth = currentMonth
    let newYear = currentYear
    
    if (direction === 'prev') {
      newMonth = currentMonth === 0 ? 11 : currentMonth - 1
      newYear = currentMonth === 0 ? currentYear - 1 : currentYear
    } else {
      newMonth = currentMonth === 11 ? 0 : currentMonth + 1
      newYear = currentMonth === 11 ? currentYear + 1 : currentYear
    }
    
    setCurrentMonth(newMonth)
    setCurrentYear(newYear)
    onMonthChange?.(newMonth, newYear)
    
    if (newYear !== currentYear) {
      onYearChange?.(newYear)
    }
  }, [currentMonth, currentYear, onMonthChange, onYearChange])
  
  // Quick actions
  const handleToday = useCallback(() => {
    const today = new Date()
    setCurrentMonth(today.getMonth())
    setCurrentYear(today.getFullYear())
    handleDateChange(today)
  }, [handleDateChange])
  
  const handleClear = useCallback(() => {
    handleDateChange(null)
  }, [handleDateChange])
  
  // Keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!isOpen) return
    
    switch (event.key) {
      case 'Escape':
        setIsOpen(false)
        onBlur?.()
        break
      case 'Enter':
        if (selectedDate) {
          setIsOpen(false)
          onBlur?.()
        }
        break
      case 'ArrowLeft':
        event.preventDefault()
        navigateMonth('prev')
        break
      case 'ArrowRight':
        event.preventDefault()
        navigateMonth('next')
        break
    }
  }, [isOpen, selectedDate, navigateMonth, onBlur])
  
  // Effect: Sync external value changes
  useEffect(() => {
    if (value !== undefined) {
      setSelectedDate(value)
      if (value) {
        setCurrentMonth(value.getMonth())
        setCurrentYear(value.getFullYear())
      }
    }
  }, [value])
  
  // Effect: Track interaction start time
  useEffect(() => {
    if (isOpen && trackInteraction) {
      interactionStartTime.current = Date.now()
    }
  }, [isOpen, trackInteraction])
  
  // Cleanup undo timeout on unmount
  useEffect(() => {
    return () => {
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
    }
  }, [undoState.timeout])
  
  // Style classes based on BIRHAUS tokens
  const sizeClasses = {
    sm: 'text-sm p-2',
    md: 'text-base p-3',
    lg: 'text-lg p-4'
  }
  
  const variantClasses = {
    default: 'bg-white border border-gray-200 rounded-lg shadow-md',
    minimal: 'bg-transparent border-none',
    inline: 'bg-gray-50 border border-gray-100 rounded-md'
  }
  
  return (
    <div 
      ref={ref}
      className={cn(
        'birhaus-calendar relative',
        safeAccess(sizeClasses, size, sizeClasses.md),
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {/* Label */}
      {displayLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {displayLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Input Display */}
      <div 
        className={cn(
          'flex items-center justify-between cursor-pointer',
          'border rounded-md px-3 py-2 bg-white',
          error || displayError ? 'border-red-300' : 'border-gray-300',
          'hover:border-gray-400 focus-within:border-primary-600',
          'transition-colors duration-200',
          disabled && 'cursor-not-allowed'
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onFocus={onFocus}
      >
        <span className={cn(
          selectedDate ? 'text-gray-900' : 'text-gray-500'
        )}>
          {selectedDate ? formatDate(selectedDate) : displayPlaceholder}
        </span>
        <Calendar className="h-4 w-4 text-gray-400" />
      </div>
      
      {/* Error Message */}
      {displayError && (
        <p className="mt-1 text-sm text-red-600">
          {displayError}
        </p>
      )}
      
      {/* Calendar Popup */}
      {isOpen && (
        <div className={cn(
          'absolute top-full left-0 z-50 mt-2',
          safeAccess(variantClasses, variant, variantClasses.default),
          'w-80 max-w-sm'
        )}>
          {/* Header with Navigation */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <button
              type="button"
              onClick={() => navigateMonth('prev')}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label="Mes anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            <div className="text-center">
              <div className="font-semibold text-gray-900">
                {safeArrayAccess(MESES_ESPANOL, currentMonth, 'Enero')} {currentYear}
              </div>
            </div>
            
            <button
              type="button"
              onClick={() => navigateMonth('next')}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label="Mes siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          {/* Days of Week Header */}
          <div className="grid grid-cols-7 gap-1 p-2 border-b border-gray-100">
            {DIAS_CORTOS_ESPANOL.slice(firstDayOfWeek).concat(
              DIAS_CORTOS_ESPANOL.slice(0, firstDayOfWeek)
            ).map((day, index) => (
              <div key={index} className="text-xs font-medium text-gray-500 text-center p-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 p-2">
            {calendarDays.map((date, index) => (
              <button
                key={index}
                type="button"
                disabled={!date || isDateDisabled(date)}
                onClick={() => date && !isDateDisabled(date) && handleDateChange(date)}
                className={cn(
                  'p-2 text-sm rounded transition-colors min-h-[36px]',
                  'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500',
                  !date && 'cursor-default',
                  date && isDateDisabled(date) && 'text-gray-400 cursor-not-allowed',
                  date && !isDateDisabled(date) && 'text-gray-900 hover:bg-primary-50',
                  date && selectedDate && date.toDateString() === selectedDate.toDateString() && 
                    'bg-primary-600 text-white hover:bg-primary-700',
                  date && new Date().toDateString() === date.toDateString() && 
                    selectedDate?.toDateString() !== date.toDateString() &&
                    'ring-1 ring-primary-600 text-primary-600'
                )}
                aria-label={date ? `Seleccionar ${date.getDate()} de ${MESES_ESPANOL[date.getMonth()]}` : undefined}
              >
                {date?.getDate()}
              </button>
            ))}
          </div>
          
          {/* Quick Actions Footer - BIRHAUS: Max 3 actions */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              {showTodayButton && (
                <button
                  type="button"
                  onClick={handleToday}
                  className="px-3 py-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Hoy
                </button>
              )}
              {showClearButton && selectedDate && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700 font-medium"
                >
                  Limpiar
                </button>
              )}
            </div>
            
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label="Cerrar calendario"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Undo Notification */}
      {undoState.isActive && (
        <div className="absolute bottom-full left-0 mb-2 bg-gray-800 text-white text-sm rounded-md p-3 shadow-lg z-50 flex items-center space-x-2">
          <span>Fecha cambiada a {formatDate(selectedDate)}</span>
          <button
            type="button"
            onClick={handleUndo}
            className="flex items-center space-x-1 text-primary-200 hover:text-primary-100 font-medium"
          >
            <RotateCcw className="h-3 w-3" />
            <span>Deshacer</span>
          </button>
        </div>
      )}
      
      {/* Accessibility Description */}
      {displayDescribedBy && (
        <div id={displayDescribedBy} className="sr-only">
          {displayDescribedBy}
        </div>
      )}
    </div>
  )
})

BirhausCalendar.displayName = 'BirhausCalendar'

// Hook for enhanced calendar functionality
export const useBirhausCalendar = (initialDate?: Date | null) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate || null)
  const [undoHistory, setUndoHistory] = useState<Date[]>([])
  
  const selectDate = useCallback((date: Date | null) => {
    if (selectedDate) {
      setUndoHistory(prev => [...prev.slice(-4), selectedDate]) // Keep last 5 for undo
    }
    setSelectedDate(date)
  }, [selectedDate])
  
  const undoLastSelection = useCallback(() => {
    if (undoHistory.length > 0) {
      const previousDate = undoHistory[undoHistory.length - 1]
      setSelectedDate(previousDate)
      setUndoHistory(prev => prev.slice(0, -1))
      return previousDate
    }
    return null
  }, [undoHistory])
  
  const clearDate = useCallback(() => {
    if (selectedDate) {
      setUndoHistory(prev => [...prev.slice(-4), selectedDate])
    }
    setSelectedDate(null)
  }, [selectedDate])
  
  return {
    selectedDate,
    selectDate,
    clearDate,
    undoLastSelection,
    canUndo: undoHistory.length > 0,
    undoHistory
  }
}

// Utility functions for date operations
export const createBirhausDateRange = (start: Date, end: Date): Date[] => {
  const dates: Date[] = []
  const currentDate = new Date(start)
  
  while (currentDate <= end) {
    dates.push(new Date(currentDate))
    currentDate.setDate(currentDate.getDate() + 1)
  }
  
  return dates
}

export const getBirhausWeekdays = (exclude: number[] = []): number[] => {
  return [1, 2, 3, 4, 5].filter(day => !exclude.includes(day)) // Monday to Friday
}

export const getBirhausWeekends = (): number[] => {
  return [0, 6] // Sunday and Saturday
}